import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Check, ChevronDown } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';
import { RECRUIT_AXES, summariseRecruitScores, useRecruitScores } from '../recruitScore';
import type { RecruitTier } from '../recruitScore';
import { RECRUIT_STAGES, useRecruitStage, writeRecruitStage } from '../recruitStage';
import { useCanEditClientNotes } from '../clientNotesStore';
import RecruitStageDot from './RecruitStageDot';

/**
 * The 招募 view's two header pills, in place of 未開戶 and 潛力 — account
 * connection and the server's C.P.I. say nothing about a candidate.
 *
 *   評估 ● 80 — the 候選人評估 total out of 100 (dot = band). Opens 招募 IDEA,
 *               where the radar, band and advice are read.
 *   ● 洽談中 ⌄ — the recruitment stage, picked from a menu.
 *
 * Both follow edits made elsewhere in the drawer (useRecruitScores / useRecruitStage).
 */

const TIER_DOT: Record<RecruitTier, string> = {
  priority: '#4ade80',
  nurture: '#f59e0b',
  hold: '#94a3b8'
};

export type RecruitHeaderMarkersProps = {
  clientId: string;
  intl: IntlLike;
  onOpenIdea: () => void;
};

const RecruitHeaderMarkers = (props: RecruitHeaderMarkersProps) => {
  const { clientId, intl, onOpenIdea } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const summary = summariseRecruitScores(useRecruitScores(clientId));
  const stage = useRecruitStage(clientId);
  // No factsheet, nowhere to save a stage: the pill still shows it, but does
  // not open a menu whose every pick would be dropped.
  const canSave = useCanEditClientNotes(clientId);
  const [stageAnchor, setStageAnchor] = useState<HTMLElement | null>(null);

  // Out of 100, bare: 「評估 ● 28」. Rated-so-far keeps its fraction, since that
  // one IS a count of five.
  const scoreText =
    summary.total !== null
      ? String(summary.total)
      : summary.rated > 0
      ? `${summary.rated}/${RECRUIT_AXES.length}`
      : '—';

  return (
    <>
      <button
        type="button"
        className="clientv2-cpitag clientv2-recruit-marker"
        data-testid="drawer-recruit-score"
        data-total={summary.total ?? undefined}
        data-tier={summary.tier ?? undefined}
        onClick={onOpenIdea}
        aria-label={`${t('clientv2-recruit-marker-score')}: ${scoreText}`}
      >
        {t('clientv2-recruit-marker-score')}
        {summary.tier && <i className="clientv2-dot" style={{ backgroundColor: TIER_DOT[summary.tier] }} />}
        {/* No chevron: at 375px the pair only fits on one line without it.
            階段 keeps its ⌄, because a menu needs saying. */}
        <span>{scoreText}</span>
      </button>

      <button
        type="button"
        className="clientv2-cpitag clientv2-recruit-marker"
        data-testid="drawer-recruit-stage"
        data-stage={stage ?? undefined}
        aria-haspopup="menu"
        aria-expanded={!!stageAnchor}
        aria-label={`${t('clientv2-recruit-marker-stage')}: ${stage ? t(`clientv2-recruit-stage-${stage}`) : '—'}`}
        disabled={!canSave}
        title={canSave ? undefined : t('clientv2-notes-no-factsheet')}
        onClick={event => setStageAnchor(event.currentTarget)}
      >
        {/* 「階段」 only while unset: 已約見 names itself, and without the word the
            pair stays on one line at 375px even for a four-character stage. */}
        {stage ? (
          <>
            <RecruitStageDot stage={stage} />
            <span>{t(`clientv2-recruit-stage-${stage}`)}</span>
          </>
        ) : (
          <>
            {t('clientv2-recruit-marker-stage')}
            <span>—</span>
          </>
        )}
        <ChevronDown size={12} aria-hidden="true" />
      </button>

      <Menu
        anchorEl={stageAnchor}
        open={!!stageAnchor}
        onClose={() => setStageAnchor(null)}
        data-testid="drawer-recruit-stage-menu"
      >
        {RECRUIT_STAGES.map(option => (
          <MenuItem
            key={option}
            selected={option === stage}
            data-testid="drawer-recruit-stage-option"
            data-stage={option}
            onClick={() => {
              // Picking the current stage again clears it.
              writeRecruitStage(clientId, option === stage ? null : option);
              setStageAnchor(null);
            }}
          >
            <RecruitStageDot stage={option} className="clientv2-recruit-stage-dot" />
            {t(`clientv2-recruit-stage-${option}`)}
            {option === stage && <Check size={14} className="clientv2-recruit-stage-check" aria-hidden="true" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RecruitHeaderMarkers;
